import { useMemo, useState } from 'react';
import NavBar from '../components/NavBar';
import { useThemeStore } from '../store/themeStore';
import { getDesignTokens } from '../utils/designTokens';

interface PreviewSize {
  id: string;
  label: string;
  width: number;
  height: number;
}

const previewSizes: PreviewSize[] = [
  { id: 'iphone-se1', label: 'iPhone SE (320×568)', width: 320, height: 568 },
  { id: 'iphone-8', label: 'iPhone 8 / SE2 (375×667)', width: 375, height: 667 },
  { id: 'iphone-12mini', label: 'iPhone 12 mini (360×780)', width: 360, height: 780 },
  { id: 'iphone-12-13', label: 'iPhone 12/13/14 (390×844)', width: 390, height: 844 },
  { id: 'iphone-8-plus', label: 'iPhone 8 Plus (414×736)', width: 414, height: 736 },
];

const previewRoutes = [
  { id: 'mobile-today', label: 'Mobile Today', path: '/mobile/today' },
  { id: 'mobile-calendar', label: 'Mobile Calendar', path: '/mobile/calendar' },
  { id: 'mobile-todos', label: 'Mobile To Dos', path: '/mobile/todos' },
  { id: 'mobile-create', label: 'Mobile Create', path: '/mobile/create' },
  { id: 'mobile-family', label: 'Mobile Family', path: '/mobile/family' },
  { id: 'mobile-whats-new', label: "Mobile What's New", path: '/mobile/whats-new' },
  { id: 'auth-login', label: 'Login', path: '/login' },
  { id: 'auth-signup', label: 'Signup', path: '/signup' },
  { id: 'auth-forgot', label: 'Forgot Password', path: '/forgot-password' },
  { id: 'auth-reset', label: 'Reset Password', path: '/reset-password' },
  { id: 'auth-change', label: 'Change Password', path: '/change-password' },
];

export default function MobilePreview() {
  const { isDarkMode } = useThemeStore();
  const tokens = getDesignTokens(isDarkMode);
  const [selectedRoute, setSelectedRoute] = useState(previewRoutes[0].path);

  const selectedLabel = useMemo(() => {
    return previewRoutes.find((route) => route.path === selectedRoute)?.label ?? 'Preview';
  }, [selectedRoute]);

  return (
    <div className={`min-h-screen ${tokens.bg.gradient} transition-colors duration-200`}>
      <NavBar className={`${tokens.card.bg} ${tokens.border.light} border-b shadow-sm`} containerClassName="px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between w-full items-center gap-4">
          <div>
            <h1 className={`text-xl font-bold ${tokens.text.primary}`}>Mobile Preview</h1>
            <p className={`text-xs ${tokens.text.muted}`}>Open multiple phone sizes side-by-side.</p>
          </div>
          <a
            href={selectedRoute}
            target="_blank"
            rel="noreferrer"
            className={`${tokens.button.secondary} px-4 h-12 rounded-sm text-sm font-medium min-w-[140px] flex items-center justify-center transition-all active:scale-95`}
          >
            Open Current
          </a>
        </div>
      </NavBar>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className={`${tokens.card.bg} ${tokens.card.border} border rounded-sm p-4 ${tokens.card.shadow}`}>
          <label className={`block text-sm font-semibold ${tokens.text.primary} mb-2`} htmlFor="preview-route">
            Screen to preview
          </label>
          <div className="flex flex-wrap gap-2">
            {previewRoutes.map((route) => (
              <button
                key={route.id}
                type="button"
                onClick={() => setSelectedRoute(route.path)}
                className={`px-3 py-2 rounded-landing text-sm font-semibold transition-colors min-h-[44px] ${
                  selectedRoute === route.path
                    ? tokens.button.primary
                    : `${tokens.card.bg} ${tokens.border.light} border ${tokens.text.primary}`
                }`}
                aria-pressed={selectedRoute === route.path}
              >
                {route.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          {previewSizes.map((size) => (
            <div key={size.id} className={`${tokens.card.bg} ${tokens.card.border} border rounded-sm p-4 ${tokens.card.shadow}`}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h2 className={`text-base font-semibold ${tokens.text.primary}`}>{size.label}</h2>
                  <p className={`text-xs ${tokens.text.muted}`}>{selectedLabel}</p>
                </div>
                <span className={`text-xs ${tokens.text.muted}`}>
                  {size.width}×{size.height}
                </span>
              </div>
              <div className="overflow-auto">
                <div
                  className="rounded-landing border border-gray-300 bg-white shadow-inner"
                  style={{ width: `${size.width}px`, height: `${size.height}px` }}
                >
                  <iframe
                    title={`${selectedLabel} ${size.label}`}
                    src={selectedRoute}
                    className="w-full h-full rounded-landing"
                    style={{ border: 'none' }}
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
